import React, { useEffect, useState } from "react";
import styles from "../../Application.module.scss";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";
import EquipmentImage from "../../../../../../images/v2/equipnent_placeholder.png";
import useCommonStore from "../../../../stores/common";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { formatCurrency } from "../../../../../components/helper/helper";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";
import { getServiceClubFee } from "../../../../utils/serviceClubPricing";

const TerminalTypes = [
  {
    value: "pax-a920",
    label: "Pax A920",
  },
  {
    value: "pax-a80",
    label: "Pax A80",
  },
  {
    value: "dejavoo-z9",
    label: "Dejavoo z9",
  },
  {
    value: "dejavoo-qd4",
    label: "Dejavoo QD4",
  },
  {
    value: "dejavoo-qd2",
    label: "Dejavoo QD2",
  },
  {
    value: "dejavoo-z8",
    label: "Dejavoo z8",
  },
  {
    value: "bbpos",
    label: "BBPOS 3-in-1 Bluetooth Card Reader",
  },
  {
    value: "payanywhere-smart-terminal-mini",
    label: "Payanywhere Smart Terminal Mini",
  },
  {
    value: "payanywhere-smart-flex",
    label: "Payanywhere Smart Flex",
  },
  {
    value: "payanywhere-smart-pos",
    label: "Payanywhere Smart POS+",
  },
  {
    value: "payanywhere-smart-terminal",
    label: "PayAnywhere Smart Terminal",
  },
  {
    value: "payanywhere-smart-keypad",
    label: "PayAnywhere Smart Keypad",
  },
  {
    value: "payanywhere-mobile-card-reader",
    label: "PayAnywhere 3-in-1 Mobile Card Reader",
  },
  {
    value: "VAR",
    label: "VAR",
  },
  {
    value: "VL100",
    label: "VL100 (Standard Terminal)",
  },
  {
    value: "VL110",
    label: "VL110 (Wireless Terminal)",
  },
  {
    value: "VL500",
    label: "VL500 (Pro Terminal)",
  },
  {
    value: "VL300",
    label: "VL300 (Pin Pad)",
  },
];

const GatewayTypes = [
  {
    value: "authorize_net",
    label: "Authorize.net",
  },
  {
    value: "authorizenet",
    label: "Authorize.net",
  },
  {
    value: "nmi-gateway",
    label: "NMI",
  },
  {
    value: "nmi",
    label: "NMI",
  },
  {
    value: "usa_e_pay",
    label: "USAePay",
  },
  {
    value: "usaepay-gateway",
    label: "USAePay",
  },
  {
    value: "paysley",
    label: "Paysley",
  },
  {
    value: "paysley-gateway",
    label: "Paysley",
  },
  {
    value: "VAR",
    label: "VAR",
  },
];

function Equipment({ appData }) {
  const expandAppDetails = useCommonStore((state) => state.expandDetails);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery(768);
  const [purchasedEquipments, setPurchasedEquipments] = useState([]);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  // monthly, rent, lease
  const [rentedEquipments, setRentedEquipments] = useState([]);
  const [monthlyEquipments, setMonthlyEquipments] = useState([]);
  const [leasedEquipments, setLeasedEquipments] = useState([]);
  const [monthlyTotal, setMonthlyTotal] = useState(0);

  const [tppEquipments, setTppEquipments] = useState([]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    let equipments = appData?.ems_boot_equipments;
    if (equipments) {
      let purchases = equipments.filter(
        (equipment) => equipment.purchase_for !== null,
      );
      setPurchasedEquipments(purchases);
      let purchase_total = purchases.reduce((acc, current) => {
        acc += current.purchase_for * current.quantity;
        return acc;
      }, 0);
      setPurchaseTotal(purchase_total);

      let rent = equipments.filter((equipment) => equipment.rent_for !== null);
      setRentedEquipments(rent);
      let rent_total = rent.reduce((acc, current) => {
        acc += current.rent_for * current.quantity;
        return acc;
      }, 0);

      let monthly = equipments.filter(
        (equipment) =>
          equipment.monthly_fee !== null ||
          getServiceClubFee(equipment) !== null,
      );
      setMonthlyEquipments(monthly);
      let monthly_total = monthly.reduce((acc, current) => {
        acc +=
          (getServiceClubFee(current) ?? current.monthly_fee) * current.quantity;
        return acc;
      }, 0);

      let lease = equipments.filter(
        (equipment) => equipment.lease_for !== null,
      );
      setLeasedEquipments(lease);

      let lease_total = lease.reduce((acc, current) => {
        acc += current.lease_for * current.quantity;
        return acc;
      }, 0);

      setMonthlyTotal(rent_total + monthly_total + lease_total);

      let tpp = equipments.filter((equipment) => equipment.tpp == "yes");
      setTppEquipments(tpp);
    }
  }, [JSON.stringify(appData)]);

  const getTerminal = (key) => {
    let terminal_name = null;
    TerminalTypes.forEach((item, index) => {
      if (item.value == key) {
        terminal_name = TerminalTypes[index].label;
      }
    });
    return terminal_name;
  };

  const getGateway = (key) => {
    let gateway_name = null;
    GatewayTypes.forEach((item, index) => {
      if (item.value == key) {
        gateway_name = GatewayTypes[index].label;
      }
    });
    return gateway_name;
  };

  return (
    <div
      className={`${isMobile ? styles.mobile_business_info : styles.equipment} ${expandAppDetails ? styles.expanded : ""}`}
    >
      <div className={isMobile ? styles.business_info_header : styles.head} onClick={toggleExpanded}>
        {isMobile ? (
          <>
            <CaretRight24 className={`${styles.caret_icon} ${expanded ? styles.expanded : ""}`} />
            <h4>Equipment</h4>
          </>
        ) : (
          <>
            <ButtonIcon>
              <CaretRight24 className={expanded ? styles.expanded : ""} />
            </ButtonIcon>
            <h5>Equipment</h5>
          </>
        )}
      </div>

      <div className={`${isMobile ? styles.mobile_content : styles.body} ${expanded ? (isMobile ? styles.expanded : styles.body_expanded) : ""}`}>
        {expanded && (
          <>
            {!appData?.billing ? (
              <>
                <div>
                  <ul>
                    <li>
                      <div>
                        <p>Equipment Type</p>
                        <p>
                          {appData?.powerappequipment?.equipment_option ==
                          "terminal"
                            ? "Terminal"
                            : "Gateway"}
                        </p>
                      </div>
                    </li>
                    <li>
                      <div>
                        <p>Equipment paid for by</p>
                        <p>
                          {appData?.powerappequipment?.gateway_option ===
                          "paymentcloud_provided"
                            ? "Merchant"
                            : "Agent"}
                        </p>
                      </div>
                    </li>

                    {appData?.powerappequipment?.equipment_option ===
                      "terminal" && (
                      <li>
                        <div>
                          <p>Terminal Type</p>
                          <p>
                            {appData?.powerappequipment?.equipment_type?.trim()
                              ? appData.powerappequipment.equipment_type
                              : getTerminal(
                                  appData?.powerappequipment?.terminal_name,
                                )}
                          </p>
                        </div>
                      </li>
                    )}

                    {appData?.powerappequipment?.equipment_option ===
                      "gateway" && (
                      <li>
                        <div>
                          <p>Gateway Type</p>
                          <p>
                            {appData?.powerappequipment?.equipment_type?.trim()
                              ? appData.powerappequipment.equipment_type
                              : getGateway(
                                  appData?.powerappequipment?.gateway_name,
                                )}
                          </p>
                        </div>
                      </li>
                    )}

                    {appData?.powerappequipment?.special_instruction && (
                      <li>
                        <div>
                          <p>Special Instruction</p>
                          <p>
                            {appData?.powerappequipment?.special_instruction}
                          </p>
                        </div>
                      </li>
                    )}

                    {appData?.powerappequipment?.equipment_option ===
                      "terminal" && (
                      <li>
                        <div>
                          <p>Shipping Address</p>
                          <p>
                            {appData?.powerappequipment?.merchant_dba_address}
                          </p>
                        </div>
                      </li>
                    )}

                    {appData?.powerappequipment?.gateway_id && (
                      <li>
                        <div>
                          <p>Gateway Id</p>
                          <p>{appData?.powerappequipment?.gateway_id}</p>
                        </div>
                      </li>
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <>
                {/* Purchase */}
                {purchasedEquipments.length > 0 && (
                  <div>
                    <HeadingWithLine text="Purchases" line={false} />
                    <div className={styles.equipment_list}>
                      {purchasedEquipments.map((equipment, index) => {
                        let equipment_data = equipment?.terminal
                          ? equipment?.terminal
                          : equipment?.gateway;
                        return (
                          <div
                            key={index}
                            className={styles.equipment_list_item}
                          >
                            <div>
                              <div className={styles.left}>
                                <img
                                  src={`/get-image-by-path?path=${equipment_data?.equipment_image}`}
                                />
                                <div className={styles.equipment_nq}>
                                  <span>{equipment_data?.equipment_name}</span>
                                  <span>X {equipment?.quantity} </span>
                                </div>
                              </div>
                              <div className={styles.right}>
                                $ {formatCurrency(equipment?.purchase_for)}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className={styles.total}>
                        <span>Total</span>
                        <span>$ {formatCurrency(purchaseTotal)}</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* end Purchase */}

                {/* Monthly */}
                {(rentedEquipments.length > 0 ||
                  leasedEquipments.length > 0 ||
                  monthlyEquipments.length > 0) && (
                  <div>
                    <HeadingWithLine text="Monthly cost" line={false} />
                    <div className={styles.equipment_list}>
                      {rentedEquipments.map((equipment, index) => {
                        let equipment_data = equipment?.terminal
                          ? equipment?.terminal
                          : equipment?.gateway;
                        return (
                          <div
                            key={index}
                            className={styles.equipment_list_item}
                          >
                            <div>
                              <div className={styles.left}>
                                <img
                                  src={`/get-image-by-path?path=${equipment_data?.equipment_image}`}
                                />
                                <div className={styles.equipment_nq}>
                                  <span>
                                    {equipment_data?.equipment_name}{" "}
                                    <span className={styles.badge}>Rent</span>{" "}
                                  </span>
                                  <span>X {equipment?.quantity} </span>
                                </div>
                              </div>
                              <div className={styles.right}>
                                $ {formatCurrency(equipment?.rent_for)}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {leasedEquipments.map((equipment, index) => {
                        let equipment_data = equipment?.terminal
                          ? equipment?.terminal
                          : equipment?.gateway;
                        return (
                          <div
                            key={index}
                            className={styles.equipment_list_item}
                          >
                            <div>
                              <div className={styles.left}>
                                <img
                                  src={`/get-image-by-path?path=${equipment_data?.equipment_image}`}
                                />
                                <div className={styles.equipment_nq}>
                                  <span>
                                    {equipment_data?.equipment_name}{" "}
                                    <span className={styles.badge}>Lease</span>{" "}
                                  </span>
                                  <span>X {equipment?.quantity} </span>
                                </div>
                              </div>
                              <div className={styles.right}>
                                $ {formatCurrency(equipment?.lease_for)}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {monthlyEquipments.map((equipment, index) => {
                        let equipment_data = equipment?.terminal
                          ? equipment?.terminal
                          : equipment?.gateway;
                        return (
                          <div
                            key={index}
                            className={styles.equipment_list_item}
                          >
                            <div>
                              <div className={styles.left}>
                                <img
                                  src={`/get-image-by-path?path=${equipment_data?.equipment_image}`}
                                />
                                <div className={styles.equipment_nq}>
                                  <span>
                                    {equipment_data?.equipment_name}{" "}
                                    <span className={styles.badge}>
                                      Monthly
                                    </span>{" "}
                                  </span>
                                  <span>X {equipment?.quantity} </span>
                                </div>
                              </div>
                              <div className={styles.right}>
                                ${" "}
                                {formatCurrency(
                                  getServiceClubFee(equipment) ??
                                    equipment?.monthly_fee,
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className={styles.total}>
                        <span>Total</span>
                        <span>$ {formatCurrency(monthlyTotal)}</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* end Monthly */}

                {/* TPP */}
                {tppEquipments.length > 0 && (
                  <div>
                    <HeadingWithLine text="TPP Hardware" line={false} />
                    <div className={styles.equipment_list}>
                      {/* Items */}
                      {tppEquipments.map((equipment, index) => {
                        let equipment_data = equipment?.terminal
                          ? equipment?.terminal
                          : equipment?.gateway;
                        return (
                          <div
                            key={index}
                            className={styles.equipment_list_item}
                          >
                            <div>
                              <div className={styles.left}>
                                <img
                                  src={`/get-image-by-path?path=${equipment_data?.equipment_image}`}
                                />
                                <div className={styles.equipment_nq}>
                                  <span>{equipment_data?.equipment_name}</span>
                                  <span>X {equipment?.quantity} </span>
                                </div>
                              </div>
                              <div className={`${styles.tpp_price}`}>
                                <span>$ 0.00</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* end Items */}
                    </div>
                  </div>
                )}
                {/* end TPP */}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Equipment;
